import { useState } from 'react';
import { GitBranch, ChevronDown, Check, Plus } from 'lucide-react';
import clsx from 'clsx';

interface BranchSelectorProps {
  branches: string[];
  currentBranch: string;
  onChange: (branch: string) => void;
  onCreateBranch?: (name: string) => void;
}

export function BranchSelector({
  branches,
  currentBranch,
  onChange,
  onCreateBranch,
}: BranchSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');

  const handleCreate = () => {
    if (newBranchName.trim() && onCreateBranch) {
      onCreateBranch(newBranchName.trim());
      setNewBranchName('');
      setShowCreate(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50"
      >
        <GitBranch className="w-4 h-4 text-gray-500" />
        <span className="font-medium">{currentBranch}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white border rounded-lg shadow-lg z-20">
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Find or create a branch..."
              className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={newBranchName}
              onChange={(e) => setNewBranchName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreate();
                }
              }}
            />
          </div>

          <div className="max-h-64 overflow-auto py-1">
            {branches
              .filter((b) =>
                b.toLowerCase().includes(newBranchName.toLowerCase())
              )
              .map((branch) => (
                <button
                  key={branch}
                  onClick={() => {
                    onChange(branch);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-4 py-2 text-left text-sm hover:bg-gray-50',
                    branch === currentBranch && 'bg-primary-50'
                  )}
                >
                  <GitBranch className="w-4 h-4 text-gray-400" />
                  <span className="flex-1 truncate">{branch}</span>
                  {branch === currentBranch && (
                    <Check className="w-4 h-4 text-primary-600" />
                  )}
                </button>
              ))}

            {newBranchName &&
              !branches.some(
                (b) => b.toLowerCase() === newBranchName.toLowerCase()
              ) &&
              onCreateBranch && (
                <button
                  onClick={handleCreate}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-primary-600 hover:bg-primary-50 border-t"
                >
                  <Plus className="w-4 h-4" />
                  Create branch "{newBranchName}"
                </button>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
